import * as React from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import cx from 'classnames';
import Button from 'components/ButtonDeprecated';
import TextWithIcon from 'components/TextWithIcon';
import MultiSelect from 'components/MultiSelect';
import Heading from 'components/Heading';
import EventTracking from 'lib/EventTracking';
import Field from 'components/Field';
import LayerTrigger from 'components/LayerTrigger';
import Icon from 'components/IconDeprecated';
import Link from 'components/Link';
import TooltipTrigger from 'components/TooltipTrigger';
import Layer from 'components/Layer';
import { selectRecipient, RECIPIENT_TYPES } from 'reducers/roleBasedMessagingSearch';
import TargetFilterMenuItem from 'chat/components/TargetFilterMenuItem';
import TextWithTooltip from 'components/TextWithTooltip';
import CurrentUser from 'auth/CurrentUser';
import { MessageFilterType, Recipient } from 'types/typescript/messagingTypes';

import './TargetFilterMenu.scss';

import type { MouseEvent } from 'react';

type Props = {
  filters: any;
  // @ts-expect-error ts-migrate(2304) FIXME: Cannot find name 'OrganizationRecipient'.
  recipient: OrganizationRecipient | GroupRecipient;
  dataTestSelector: string | undefined | null;
  recipientIsSelected: boolean;
  // Redux state
  // @ts-expect-error ts-migrate(2304) FIXME: Cannot find name 'OrganizationRecipient'.
  selection: (OrganizationRecipient | GroupRecipient)[];
  districtIsSelected: boolean;
  // Redux dispatchers
  onSelectRecipient: (recipient: Recipient, filter?: MessageFilterType | null) => any;
};

type State = {
  activeFilters: {
    roles: any[];
    sisLinked: any[];
    grades: any[];
  };
  menuIsOpen: boolean;
};

const GradesTooltip = () => (
  <TooltipTrigger
    placement="top"
    tooltip="Recipients may not have grades in Remind. Click to learn more."
  >
    <Link
      color="graphite"
      href="https://help.remind.com/hc/en-us/articles/5407870564621"
      rel="noopener noreferrer"
      target="_blank"
    >
      <Icon name="question-circle" />
    </Link>
  </TooltipTrigger>
);

class TargetFilterMenu extends React.Component<Props, State> {
  state = {
    activeFilters: {
      roles: [],
      grades: [],
      sisLinked: [],
    },
    selectedOptions: {
      roles: [],
      grades: [],
      sisLinked: [],
    },
    menuIsOpen: false,
  };

  filterIsSelected = (filter: any) => {
    return this.getSelectedFilters(filter.type).some(
      (selectedFilter) => selectedFilter.value.id === filter.value.id
    );
  };

  getSelectedFilters = (type: string): MessageFilterType[] => {
    const recipientInSelection: any = this.props.selection.find(
      (selectedRecipient) => selectedRecipient.id === this.props.recipient.id
    );
    if (
      recipientInSelection &&
      recipientInSelection.type !== RECIPIENT_TYPES.ORGANIZATION_RECIPIENT &&
      recipientInSelection.type !== RECIPIENT_TYPES.GROUP_RECIPIENT &&
      type !== 'all'
    ) {
      return [];
    }

    if (recipientInSelection && recipientInSelection.selectedFilters) {
      return type === 'all'
        ? recipientInSelection.selectedFilters
        : recipientInSelection.selectedFilters.filter(
            // @ts-expect-error ts-migrate(7006) FIXME: Parameter 'selectedFilter' implicitly has an 'any'... Remove this comment to see the full error message
            (selectedFilter) => selectedFilter.type === type
          );
    } else {
      return [];
    }
  };

  getFilterLabel = (type: string, value: string): string => {
    // @ts-expect-error ts-migrate(7006) FIXME: Parameter 'f' implicitly has an 'any' type.
    return this.props.filters[type].find((f) => f.value.id === value)?.value.label;
  };

  deselectFullRecipient = () => {
    EventTracking.trackKey('announcements.share_to.role.unselect.click', { context: 'everyone' });
    this.props.onSelectRecipient(this.props.recipient, null);
  };

  isRoleSelectionFrozen = (isSelected: boolean) => {
    return (
      isSelected &&
      (this.getSelectedFilters('sisLinked').length > 0 ||
        this.getSelectedFilters('grades').length > 0) &&
      this.getSelectedFilters('roles').length === 1
    );
  };

  handleMenuItemSelection = (
    event: MouseEvent,
    filter: MessageFilterType,
    isIncompatible: boolean
  ) => {
    event.stopPropagation();
    if (isIncompatible || this.isRoleSelectionFrozen(this.filterIsSelected(filter))) {
      return;
    }

    const roleContext = filter.value.id;
    if (this.filterIsSelected(filter)) {
      EventTracking.trackKey('announcements.share_to.role.unselect.click', {
        context: roleContext,
      });
    } else {
      EventTracking.trackKey('announcements.share_to.role.select.click', {
        context: roleContext,
      });
    }

    if (filter.value.id === 'everyone') {
      if (this.recipientIsSelectedAndUnfiltered(filter.type)) {
        this.deselectFullRecipient();
        return;
      } else {
        const roleFilters = this.getSelectedFilters('roles').filter(
          (r) => r.value.id !== 'everyone'
        );

        roleFilters.forEach((r) =>
          this.props.onSelectRecipient(
            this.props.recipient,
            this.makeMessageFilter('roles', {
              id: r.value.id,
              label: this.getFilterLabel('roles', r.value.id),
            })
          )
        );
        this.props.onSelectRecipient(this.props.recipient, null);
        return;
      }
    }
    this.props.onSelectRecipient(this.props.recipient, filter);
  };

  handleDropdownChangeSisLinked = (values: string[]) => {
    if (values.length) {
      this.selectUnrestrictedRoles('rostered');
    }
    const previousSisLinked =
      this.state.activeFilters.sisLinked[0] ||
      (this.props.selection[0]?.selectedFilters &&
        // @ts-expect-error ts-migrate(7006) FIXME: Parameter 'filter' implicitly has an 'any' type.
        this.props.selection[0].selectedFilters.find((filter) => filter.type === 'sisLinked')?.value
          .id) ||
      undefined;

    this.setState(
      (prevState) => ({
        ...prevState,
        activeFilters: {
          ...prevState.activeFilters,
          sisLinked: values.length > 1 ? [values[1]] : values,
        },
      }),
      () => {
        if (this.props.onSelectRecipient) {
          if (values.length > 0) {
            values.forEach((value) => {
              this.props.onSelectRecipient(
                this.props.recipient,
                this.makeMessageFilter('sisLinked', {
                  id: value,
                  label: this.getFilterLabel('rostered', value),
                })
              );
            });
          } else {
            this.props.onSelectRecipient(
              this.props.recipient,
              this.makeMessageFilter('sisLinked', {
                id: previousSisLinked,
                label: this.getFilterLabel('rostered', previousSisLinked),
              })
            );
          }
        }
      }
    );
  };

  handleDropdownChangeGrades = (values: any[]) => {
    if (values.length) {
      this.selectUnrestrictedRoles('grades');
    }

    let difference =
      this.state.activeFilters.grades.length > 0
        ? this.state.activeFilters.grades.filter((x) => !values.includes(x))
        : [];

    if (values.includes('any')) {
      // @ts-expect-error ts-migrate(2322) FIXME: Type 'any[]' is not assignable to type 'never[]'.
      difference = values.splice(values.indexOf('any', 0), 1);
      values = [];
    }

    const shouldRemove = this.state.activeFilters.grades && difference.length > 0;
    this.setState(
      (prevState) => ({
        ...prevState,
        activeFilters: {
          ...prevState.activeFilters,
          grades: values,
        },
      }),
      () => {
        if (this.props.onSelectRecipient) {
          if (shouldRemove) {
            difference.forEach((d) =>
              this.props.onSelectRecipient(
                this.props.recipient,
                this.makeMessageFilter('grades', { id: d, label: this.getFilterLabel('grades', d) })
              )
            );
          } else {
            const filterValue = values[values.length - 1];
            this.props.onSelectRecipient(
              this.props.recipient,
              this.makeMessageFilter('grades', {
                id: filterValue,
                label: this.getFilterLabel('grades', filterValue),
              })
            );
          }
        }
      }
    );
  };

  makeMessageFilter = (type: string, value: any) => {
    return { type, value };
  };

  recipientIsSelectedAndUnfiltered = (filterType: string) => {
    return this.props.recipientIsSelected && !this.getSelectedFilters(filterType).length;
  };

  selectUnrestrictedRoles = (type: string) => {
    const hasStaffRole = !!this.props.filters?.roles?.find((r: any) => r.value.id === 'staff');

    const unrestrictedRoles = (() => {
      if (type === 'grades') {
        return ['student', 'parent'];
      } else {
        if (hasStaffRole) {
          return ['student', 'parent', 'teacher', 'staff'];
        } else {
          return ['student', 'parent', 'teacher'];
        }
      }
    })();

    if (!this.getSelectedFilters('roles').length) {
      unrestrictedRoles.forEach((r) => {
        this.props.onSelectRecipient(
          this.props.recipient,
          this.makeMessageFilter('roles', {
            id: r,
            label: this.getFilterLabel('roles', r),
          })
        );
      });
    }
  };

  renderFilterMenu = (filters: any) => {
    const { recipient } = this.props;

    const newFiltersFF =
      CurrentUser.isSignedIn() &&
      CurrentUser.getUser() &&
      CurrentUser.getUser().hasFeature('2022.feb.message_builder_v1_composer_new_filters');
    // Roles Filters
    const orgUuid =
      recipient.type === RECIPIENT_TYPES.ORGANIZATION_RECIPIENT
        ? recipient.organization.uuid
        : undefined;
    const groupUuid =
      recipient.type === RECIPIENT_TYPES.GROUP_RECIPIENT ? recipient.group.uuid : undefined;
    const allFilter = this.makeMessageFilter('roles', {
      id: 'everyone',
      label: `Everyone`,
    });
    let dataTestId = '';
    if (recipient.type === RECIPIENT_TYPES.GROUP_RECIPIENT) {
      dataTestId = recipient.group.id;
    } else if (recipient.type === RECIPIENT_TYPES.ORGANIZATION_RECIPIENT) {
      dataTestId = recipient.organization.id;
    }

    const selectedGradesValues = this.getSelectedFilters('grades').map((f) => f.value.id);
    const selectedSisLinkedValues = this.getSelectedFilters('sisLinked').map((f) => f.value.id);
    const selectedRolesValues = this.getSelectedFilters('roles').map((f) => f.value.id);

    // Grade filters

    const gradeFilters =
      filters.grades && filters.grades.length > 0
        ? filters.grades
            // @ts-expect-error ts-migrate(7006) FIXME: Parameter 'f' implicitly has an 'any' type.
            .filter((f) => f.value.id !== 'NA')
            // @ts-expect-error ts-migrate(7006) FIXME: Parameter 'f' implicitly has an 'any' type.
            .map((f) => {
              return { label: f.value.label, value: f.value.id };
            })
        : [];

    // Rostered filters
    const sisLinkedFilters =
      filters.rostered && filters.rostered.length > 0
        ? // @ts-expect-error ts-migrate(7006) FIXME: Parameter 'f' implicitly has an 'any' type.
          filters.rostered.map((f) => {
            return { label: f.value.label, value: f.value.id };
          })
        : [];
    // @ts-expect-error ts-migrate(7034) FIXME: Variable 'nonRoleFiltersValues' implicitly has typ... Remove this comment to see the full error message
    const nonRoleFiltersValues = [];
    if (selectedGradesValues.length > 0) {
      nonRoleFiltersValues.push({ grades: selectedGradesValues });
    }
    if (selectedSisLinkedValues.length > 0) {
      nonRoleFiltersValues.push({ sisLinked: selectedSisLinkedValues });
    }

    const rosteredIncompatibleRoles = ['admin'];
    const gradesIncompatibleRoles = ['staff', 'admin', 'teacher'];

    const rosteredIncompatibleRoleSelected = selectedRolesValues.some((r) =>
      rosteredIncompatibleRoles.includes(r)
    );
    const gradesIncompatibleRoleSelected = selectedRolesValues.some((r) =>
      gradesIncompatibleRoles.includes(r)
    );

    const sislinkedSelect = (disabled: boolean) => (
      <MultiSelect
        aria-label="SIS Link"
        key="rostered"
        hideSelectedOptions={false}
        options={sisLinkedFilters}
        searchable={false}
        value={this.getSelectedFilters('sisLinked').map((f) => {
          return { label: f.value.label, value: f.value.id };
        })}
        data-test="filter-select-sislink"
        closeMenuOnSelect={false}
        onChange={this.handleDropdownChangeSisLinked}
        disabled={disabled}
      />
    );

    const isRosteredIncompatible = (filterId: string) => {
      return (
        selectedSisLinkedValues.length > 0 &&
        (filterId === 'all' || rosteredIncompatibleRoles.includes(filterId))
      );
    };

    const isGradesIncompatible = (filterId: string) => {
      return (
        selectedGradesValues.length > 0 &&
        (filterId === 'all' || gradesIncompatibleRoles.includes(filterId))
      );
    };

    const isIncompatible = (filterId: string) => {
      return isRosteredIncompatible(filterId) || isGradesIncompatible(filterId);
    };

    const gradesSelect = (disabled: boolean) => (
      <MultiSelect
        aria-label="Grade"
        key="grades"
        hideSelectedOptions={false}
        options={gradeFilters}
        searchable={false}
        value={this.getSelectedFilters('grades').map((f) => {
          return { label: f.value.label, value: f.value.id };
        })}
        data-test="filter-select-grade"
        closeMenuOnSelect={false}
        onChange={this.handleDropdownChangeGrades}
        disabled={disabled}
        name="grades"
      />
    );

    return (
      // @ts-expect-error ts-migrate(7006) FIXME: Parameter 'e' implicitly has an 'any' type.
      <Layer className="TargetFilterMenu-filters" onClick={(e) => e.stopPropagation()}>
        <div className="TargetFilterMenu-filters-roles">
          <Heading level="3">Roles</Heading>
          <TargetFilterMenuItem
            // @ts-expect-error ts-migrate(2322) FIXME: Type '{ filter: { type: string; value: any; }; non... Remove this comment to see the full error message
            filter={allFilter}
            nonRoleFilters={nonRoleFiltersValues.map((x) => x)}
            isSelected={this.recipientIsSelectedAndUnfiltered('roles')}
            orgUuid={orgUuid}
            groupUuid={groupUuid}
            // @ts-expect-error ts-migrate(7006) FIXME: Parameter 'e' implicitly has an 'any' type.
            onClick={(e) => this.handleMenuItemSelection(e, allFilter, isIncompatible('all'))}
            rosteredIncompatible={isRosteredIncompatible('all')}
            gradesIncompatible={isGradesIncompatible('all')}
          />
          {filters.roles &&
            filters.roles.length > 0 &&
            // @ts-expect-error ts-migrate(7006) FIXME: Parameter 'f' implicitly has an 'any' type.
            filters.roles.map((f) => {
              const roleFilter = this.makeMessageFilter('roles', f.value);
              return (
                <TargetFilterMenuItem
                  key={f.value.id}
                  // @ts-expect-error ts-migrate(2322) FIXME: Type '{ key: any; filter: { type: string; value: a... Remove this comment to see the full error message
                  filter={roleFilter}
                  // @ts-expect-error ts-migrate(7005) FIXME: Variable 'nonRoleFiltersValues' implicitly has an ... Remove this comment to see the full error message
                  nonRoleFilters={nonRoleFiltersValues.map((x) => x)}
                  isSelected={this.filterIsSelected(roleFilter)}
                  orgUuid={orgUuid}
                  groupUuid={groupUuid}
                  dataTestSelector={`split-composer-group-filter-${dataTestId}`}
                  // @ts-expect-error ts-migrate(7006) FIXME: Parameter 'e' implicitly has an 'any' type.
                  onClick={(e) =>
                    this.handleMenuItemSelection(e, roleFilter, isIncompatible(roleFilter.value.id))
                  }
                  rosteredIncompatible={isRosteredIncompatible(roleFilter.value.id)}
                  gradesIncompatible={isGradesIncompatible(roleFilter.value.id)}
                />
              );
            })}
        </div>
        {newFiltersFF && (sisLinkedFilters.length > 0 || gradeFilters.length > 0) && (
          <div className="TargetFilterMenu-filters-secondary">
            {sisLinkedFilters.length > 0 && (
              <Field key={'SIS Link Filter'}>
                <Heading level="3">SIS Link</Heading>
                {rosteredIncompatibleRoleSelected ? (
                  <TextWithTooltip
                    tooltip="Only students, parents, teachers, and staff can be filtered by SIS link."
                    tooltipPlacement="bottom"
                    // @ts-expect-error ts-migrate(2322) FIXME: Type '{ children: Element; tooltip: string; toolti... Remove this comment to see the full error message
                    weight="normal"
                    color="paper"
                  >
                    {sislinkedSelect(true)}
                  </TextWithTooltip>
                ) : (
                  sislinkedSelect(false)
                )}
              </Field>
            )}
            {gradeFilters.length > 0 && (
              <Field key={'Grade Filter'}>
                <Heading level="3">
                  Grade <GradesTooltip />
                </Heading>
                {gradesIncompatibleRoleSelected ? (
                  <TextWithTooltip
                    tooltip="Only students and parents can be filtered by grades."
                    tooltipPlacement="bottom"
                    // @ts-expect-error ts-migrate(2322) FIXME: Type '{ children: Element; tooltip: string; toolti... Remove this comment to see the full error message
                    weight="normal"
                    color="paper"
                  >
                    {gradesSelect(true)}
                  </TextWithTooltip>
                ) : (
                  gradesSelect(false)
                )}
              </Field>
            )}
          </div>
        )}
      </Layer>
    );
  };

  toggleMenu = (event: MouseEvent) => {
    event.stopPropagation();
    if (this.refs.trigger) {
      // @ts-expect-error ts-migrate(2339) FIXME: Property 'toggleLayer' does not exist on type 'Rea... Remove this comment to see the full error message
      this.refs.trigger.toggleLayer();
    }
  };

  render() {
    const { filters } = this.props;
    const { menuIsOpen } = this.state;
    const numberOfSelectedFilters = this.getSelectedFilters('roles').length;
    const showActiveFilterIcon =
      this.getSelectedFilters('grades').length > 0 ||
      this.getSelectedFilters('sisLinked').length > 0;
    const buttonClassName = 'RecipientItem-button';
    const selectedFilterText =
      numberOfSelectedFilters < 3
        ? this.getSelectedFilters('roles')
            .map((selectedFilter) => selectedFilter.value.label)
            .join(', ')
        : `${numberOfSelectedFilters} roles`;
    return (
      <LayerTrigger
        ref="trigger"
        trigger="manual"
        hideLayerOnScroll
        layer={this.renderFilterMenu(filters)}
        placement="top"
        alignment="left"
        onHide={() => {
          this.setState({
            menuIsOpen: false,
          });
          EventTracking.trackKey('announcements.share_to.refine_recipients.role.close');
        }}
        onShow={() => {
          this.setState({ menuIsOpen: true });
          // When calling on selectRecipient without a filter (second parameter is null)
          // RoleBasedMessagingSearch will catch the event and will select the recipient
          // without selecting any additional filters
          if (!this.props.recipientIsSelected) {
            this.props.onSelectRecipient(this.props.recipient, null);
          }
          EventTracking.trackKey('announcements.share_to.refine_recipients.role.open');
        }}
      >
        <Button
          onClick={this.toggleMenu}
          size="xxxsmall"
          color={this.state.menuIsOpen ? 'mascot' : 'paper'}
          weight="normal"
          data-test-selector={this.props.dataTestSelector || undefined}
          data-testid={this.props.dataTestSelector || undefined}
          className={cx(buttonClassName, menuIsOpen && `${buttonClassName}--isSelected`)}
        >
          <TextWithIcon
            appendIcon
            icon={showActiveFilterIcon ? 'filter-active' : 'filter'}
            iconClassName="TargetFilterMenu-filterButton-icon"
            // @ts-expect-error ts-migrate(2322) FIXME: Type '{ children: string; appendIcon: true; icon: ... Remove this comment to see the full error message
            figure="right"
            gutter="xsmall"
          >
            {this.recipientIsSelectedAndUnfiltered('roles') || !this.props.recipientIsSelected
              ? 'Everyone'
              : selectedFilterText}
          </TextWithIcon>
        </Button>
      </LayerTrigger>
    );
  }
}

const mapStateToProps = (state: any) => {
  return {
    districtIsSelected: state.roleBasedMessagingSearch.districtIsSelected,
    selection: state.roleBasedMessagingSearch.selection,
  };
};

const mapDispatchToProps = (dispatch: Function) => {
  return bindActionCreators(
    {
      onSelectRecipient: selectRecipient,
    },
    // @ts-expect-error ts-migrate(2769) FIXME: No overload matches this call.
    dispatch
  );
};

export { TargetFilterMenu };

export default connect(mapStateToProps, mapDispatchToProps)(TargetFilterMenu);
